// Four-Layer MOF Architecture for Peak Priorities Base Camp
// Based on OMG's Meta-Object Facility (MOF) with engines as separate infrastructure

export interface MOFLayer {
  id: string;
  level: string;
  name: string;
  description: string;
  role: string;
  examples: string[];
  color: string;
  icon: string;
  relationshipToNext: string;
}

export const mofLayers: MOFLayer[] = [
  {
    id: 'm3',
    level: 'M3',
    name: 'Meta-Model',
    description: 'The foundational representational grammar that defines how to describe anything',
    role: 'Provide the universal language for describing all models',
    examples: [
      'Ontology Layer (what exists)',
      'Modality Layer (how properties vary)',
      'Dynamics Layer (how things change)',
      'Evaluation Layer (why it matters)',
      'Categories field (Framework, Model, Tool, etc.)',
      'Structures field (Hierarchy, Network, etc.)',
      'Relationships field (is-a, part-of, etc.)',
      'Attributes field (Impact, Effort, etc.)',
      'States field (Toggle, Dial, Multi-State, etc.)',
      'Rules field (Thresholds, Weighting, etc.)'
    ],
    color: 'indigo',
    icon: 'Layers',
    relationshipToNext: 'define structure for'
  },
  {
    id: 'm2',
    level: 'M2',
    name: 'Model',
    description: 'Concrete registry entries - the actual frameworks, models, and systems',
    role: 'Define reusable models that can be instantiated as templates',
    examples: [
      'Five Stages of DEGAP (pf.degap.stages)',
      'Six Kinds of Items (pf.items.kinds)',
      'Capability Dimensions (12) (pf.framework.diagnostic.capabilities.12)',
      'Contextual Modes (STARS) (pf.framework.diagnostic.context.modes)',
      'Four-Level Diagramming Taxonomy (gf.diagram.levels)',
      'Tool Function Families (gf.tools.families)',
      'Interrogative Dimensions (5W+Extent) (idf.core.set)',
      'Four Modes of Work (pf.modes.work)',
      'Capability Families (4) (pf.framework.diagnostic.capabilities.families)'
    ],
    color: 'blue',
    icon: 'Database',
    relationshipToNext: 'instantiate as'
  },
  {
    id: 'm1',
    level: 'M1',
    name: 'Template',
    description: 'Specific templates - instances of models ready for deployment',
    role: 'Instantiate models as concrete, parameterized templates that can be deployed',
    examples: [
      'DEGAP Workshop Design (2-day) [based on Five Stages of DEGAP]',
      'Six Kinds Assessment Template [based on Six Kinds of Items]',
      'Individual Prioritization Assessment [based on 12 Capabilities]',
      'Team Capability Development Playbook [based on 12 Capabilities]',
      'STARS Context Assessment [based on STARS]',
      'Tool Selection Framework [based on Tool Function Families]',
      'Strategy Visualization Template Set [based on Four-Level Diagramming]',
      'Project Scoping Worksheet [based on 5W+Extent]',
      'Mode-Based Meeting Templates [based on Four Modes of Work]'
    ],
    color: 'coral',
    icon: 'FileEdit',
    relationshipToNext: 'deploy as'
  },
  {
    id: 'm0',
    level: 'M0',
    name: 'Instance',
    description: 'Actual runtime instances - lived experiences with specific data and measurable outcomes',
    role: 'Deliver value to end users and generate evidence/feedback',
    examples: [
      'TechCorp Q4 2024 Workshop - 12 participants, 95% satisfaction',
      'Sarah Martinez Personal Assessment - Individual, 18 minutes',
      '247 Public Assessments - October 2024, NPS 67',
      'DataFlow Engineering Team Assessment - 15 people, 6 domain scores',
      'MegaCorp Turnaround Diagnostic - 6 executives, confirmed STARS mode',
      'HealthTech Board Presentation - 45 attendees, unanimous approval',
      'Prioritization Mastery Cohort 1 - 25 students, 94% completion',
      'CloudScale Leadership Meetings - 12 weeks, 8.4/10 effectiveness'
    ],
    color: 'orange',
    icon: 'Zap',
    relationshipToNext: 'feedback flows back to M4-M3-M2-M1'
  }
];

export const mofRelationships = [
  {
    from: 'm4',
    to: 'm3',
    relationship: 'Engines orchestrate the meta-model',
    description: 'Meta-engines provide the infrastructure for creating, storing, transforming, and delivering knowledge objects described by the meta-model grammar'
  },
  {
    from: 'm3',
    to: 'm2',
    relationship: 'Meta-model defines how to describe models',
    description: 'The Meta-Stack grammar provides the fields (Ontology, Modality, Dynamics, Evaluation) used to fully specify each model'
  },
  {
    from: 'm2',
    to: 'm1',
    relationship: 'Models are instantiated as templates',
    description: 'Models (like "Five Stages of DEGAP") are instantiated as concrete templates (like "DEGAP Workshop Design")'
  },
  {
    from: 'm1',
    to: 'm0',
    relationship: 'Templates are deployed as instances',
    description: 'Templates become actual runtime instances - events, assessments, workshops with real participants and data'
  },
  {
    from: 'm0',
    to: 'm4',
    relationship: 'Instances generate feedback',
    description: 'Real-world evidence flows back up to improve engines, meta-model, models, and templates',
    isLoop: true
  }
];

export const mofValueFlow = {
  description: 'M4 orchestrate → M3 define → M2 specify → M1 instantiate → M0 realize → Feedback loops back',
  layers: [
    { level: 'M4', focus: 'Infrastructure & Orchestration' },
    { level: 'M3', focus: 'Universal Metadata (Meta-Model)' },
    { level: 'M2', focus: 'Frameworks & Systems (Model)' },
    { level: 'M1', focus: 'Templates & Deliverables (Template)' },
    { level: 'M0', focus: 'Runtime Experiences (Instance)' }
  ]
};

// Mapping old activation architecture to MOF
export const architectureMapping = {
  'Engines Layer': 'Engines - Infrastructure',
  'Meta-Design Layer (Meta-Stack)': 'M3 - Meta-Model',
  'Meta-Design Layer (Registry Entries)': 'M2 - Model',
  'Design Time Layer': 'M1 - Template',
  'Run Time Layer': 'M0 - Instance'
};

// Key insight: The Four-Layer Meta-Stack (Ontology/Modality/Dynamics/Evaluation) IS the M3 layer (Meta-Model)
export const metaStackAsM3 = {
  level: 'M3',
  name: 'Four-Layer Meta-Stack (Meta-Model)',
  purpose: 'Universal representational grammar - the meta-model that defines how to define models',
  layers: [
    {
      name: 'Ontology',
      description: 'What exists',
      provides: 'Categories, Structures, Relationships, Systems'
    },
    {
      name: 'Modality', 
      description: 'How properties vary',
      provides: 'Attributes, States, Contexts, Constraints, Identity/Persistence'
    },
    {
      name: 'Dynamics',
      description: 'How things change',
      provides: 'Rules, Processes, Causality, Uncertainty'
    },
    {
      name: 'Evaluation',
      description: 'Why it matters',
      provides: 'Intent, Utility, Observation/Measurement, Adaptation'
    }
  ]
};
